import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactJson from 'react-json-view';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';

import Field from 'types/Field';
import Section from 'types/Section';
import { NewTemplate } from 'types/Template';

const INTERVAL_BETWEEN_RENDERS_IN_MS = 500;

const transformDefaultValue = (field: Field): string[] | string | null => {
  const singleDefaultValue = field.default_value[0] || null;

  if (singleDefaultValue === null) {
    return null;
  }

  return field.default_value;
};

const transformForPreview = (template: NewTemplate): NewTemplate =>
  ({
    ...omit(template, 'reference_label', 'exam_sections'),
    exam_sections: template.exam_sections.map(
      (s) =>
        ({
          name: s.uuid,
          ...omit(s, 'name', 'uuid'),
          exam_fields: s.exam_fields.map(
            (f) =>
              ({
                ...omit(f, 'default_value', 'preview_default_value'),
                default_value: transformDefaultValue(f),
              } as Field),
          ),
        } as unknown as Section),
    ),
  } as unknown as NewTemplate);

interface JsonPreviewerProps {
  template: NewTemplate;
}

const JsonPreviewer: React.FC<JsonPreviewerProps> = ({ template }) => {
  const [cachedTemplate, setCachedTemplate] = useState<NewTemplate>(transformForPreview(template));

  const style = useMemo(
    () => ({
      fontSize: '1rem',
    }),
    [],
  );

  const debouncedSetCachedTemplate = useRef(
    debounce((template: NewTemplate) => {
      const templateWithNoDefaultValue = transformForPreview(template);
      setCachedTemplate(templateWithNoDefaultValue);
    }, INTERVAL_BETWEEN_RENDERS_IN_MS),
  ).current;

  useEffect(() => {
    debouncedSetCachedTemplate(template);
    return () => debouncedSetCachedTemplate.cancel();
  }, [debouncedSetCachedTemplate, template]);

  return <ReactJson src={cachedTemplate} style={style} />;
};

export default JsonPreviewer;
